import React from "react"
import { Button, Col, Form, InputGroup, Modal, OverlayTrigger, ListGroup, Popover } from "react-bootstrap"
import { AsyncTypeahead, Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown, FaInfoCircle } from "react-icons/fa"
import { concat, of, Subject, BehaviorSubject , Subscription } from "rxjs"
import { catchError, debounceTime, filter, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import { APIError } from "../../api/ObservableFromFetch"
import ActionButton from "../ActionButton"
import AddressInput from "../FormattedInputs/AddressInput"
import PhoneNumberMaskedInput from "../FormattedInputs/PhoneNumberMaskedInput"
import Address from "../../api/Location/LocationModel/Address"
import StateSelect from "../FormattedInputs/StateSelect"
import State from "../../api/OrderRequests/OrderRequestsModel/State"
import { StateObservable } from "../../api/OrderRequests/OrderRequestsConstraintGet"
import AutoFilterLocationsGetObservable from "../../api/Location/AutoFilterLocationsGet"
import AutoFilterLocation from "../../api/Location/LocationModel/AutoFilterLocation"
import LocationData from "../../api/Location/LocationModel/LocationData"
import LocationsGet from "../../api/Location/LocationsGet"
import { CompanyNameRegexp, PhoneRegexp, EmailRegexp } from "../../config/Constants"
import TimeZoneListGet from "../../api/Administration/TimeZoneListGet"
import TimeZoneData from "../../api/Administration/Model/TimeZoneData"
import AddressMatchModal, { AddressData } from "../AddressMatchModal"
import FormatError from "../../formatters/FormatError"
import LocationUpdate from "../../api/Location/LocationUpdate"
import LocationCreate from "../../api/Location/LocationCreate"
import { AddLocationModal } from "./AddLocationModal"
import { CheckLocationName, CheckLocationCode } from "../../api/Location/CheckLocation"
import LocationExists from "../../api/Location/LocationModel/LocationExists"
import ConfirmationModal from "../ConfirmationModal"
import CompanyInactivate from "../../api/Companies/CompanyInactivate"
import { CompanyActivationObservable } from "../../api/Administration/ActivationProcess"
import LabAccountSelection from "./LabAccountSelection"
import { ValidZipCode } from "../../formatters/FormatZipCode"

interface LocationInformationFormProps {
  portalID: number
  companyID: number
  locationID?: number
  adminMode?: boolean
  onCancel: () => void
  onSuccess?: () => void
  add: boolean
}

interface LocationInformationData {
  name: string
  code: string
  streetAddress1: string
  streetAddress2: string
  city: string
  state: string
  zip: string
  province?: string
  country?: string
  timeZoneID: number
  webSiteURL?: string
  fax: string
  officePhone: string
  locationData?: LocationData
  contactDerPhone: string
  contactDerFirstName: string
  contactDerLastName: string
  contactDerEmail: string

}

interface LocationInformationFormState extends LocationInformationData {
  loading: boolean
  saving: boolean
  retrievedLocations?: LocationData[]
  selectedLocation?: string
  locations: string[]
  statesLoading: boolean
  states: State[]
  timeZones?: TimeZoneData[]
  timeZonesLoading: boolean
  showAddLocation: boolean
  nameChecking: boolean
  nameAvailable?: boolean
  codeChecking: boolean
  codeAvailable?: boolean
  inactivating: boolean
  activating: boolean
  showInactivateConfirmation: boolean
  showLabAccountSelection: boolean
  labAccountsLoading: boolean
}

export class LocationInformationForm extends React.PureComponent<LocationInformationFormProps, LocationInformationFormState> {
  emptyData: LocationInformationData = {
    name: "",
    code: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    zip: "",
    officePhone: "",
    timeZoneID: 0,
    fax: "",
    locationData: undefined,
    contactDerPhone: "",
    contactDerFirstName: "",
    contactDerLastName: "",
    contactDerEmail: "",
  
  }

  emptyState: LocationInformationFormState = {
    ...this.emptyData,
    loading: false,
    saving: false,
    locations: [],
    retrievedLocations: undefined,
    statesLoading: false,
    states: [],
    timeZonesLoading: false,
    showAddLocation: false,
    nameChecking: false,
    nameAvailable: undefined,
    codeChecking: false,
    codeAvailable: undefined,
    inactivating: false,
    activating: false,
    showInactivateConfirmation: false,
    showLabAccountSelection: false,
    labAccountsLoading: false
  }

  state: LocationInformationFormState = { ...this.emptyState }

  typeahead = React.createRef<Typeahead<string>>()

  locationName$ = new Subject<string>()
  checkName$ = new Subject<string>()
  checkCode$ = new Subject<string>()

  statesSubscription?: Subscription
  timeZoneSubscription?: Subscription
  searchLocationsSubscription?: Subscription
  loadSubscription?: Subscription
  saveSubscription?: Subscription
  nameExistsSubscription?: Subscription
  codeExistsSubscription?: Subscription
  inactivateSubscription?: Subscription

  componentDidMount() {
    this.setState({ statesLoading: true, timeZonesLoading: true })
    this.statesSubscription = StateObservable().subscribe(
      (results: State[]) => this.setState(prevState => ({
        states: results,
        statesLoading: false,
        state: prevState.locationData ? this.mapState(prevState.locationData, results) : prevState.state
      })),
      () => { this.setState({ statesLoading: false }) }
    )

    this.timeZoneSubscription = TimeZoneListGet().subscribe(
      (data: TimeZoneData[]) => this.setState({ timeZones: data, timeZonesLoading: false }),
      (error) => this.handleAPIError(error, { timeZonesLoading: false })
    )

    this.loadLocations()

    this.loadSubscription = this.locationName$.pipe(
      map(name => name.trim()),
      switchMap(name => concat(
        of({ loading: true }),
        LocationsGet(this.props.portalID, this.props.companyID, name, true).pipe(
          map(result => ({ loading: false, result: result })),
          catchError((error) => of({ loading: false, error: error }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: LocationData[], error?: Error | APIError }) => {
        if (o.error) this.handleAPIError(o.error, {})
        const locs = !!o.result && o.result?.length > 1 ? o.result : undefined
        this.setState({
          loading: o.loading,
          retrievedLocations: locs,
          ...this.mapLocationData(o.result),
        });
      },
      (error) => this.handleAPIError(error, { loading: false })
    )

    this.nameExistsSubscription = this.checkName$.pipe(
      map(name => name.trim()),
      filter(name => this.isNameValid(name)),
      switchMap(name => concat(
        of({ checking: true }),
        CheckLocationName(this.props.companyID, name).pipe(
          map(result => ({ checking: false, result: result })),
          catchError(() => of({ checking: false }))
        )
      ))
    ).subscribe(
      (o: { checking: boolean, result?: LocationExists }) => this.setState({
        nameChecking: o.checking,
        nameAvailable: (o.result?.LocationID || 0) === 0
      }),
      (error) => this.handleAPIError(error, { nameChecking: false, nameAvailable: undefined })
    )

    this.codeExistsSubscription = this.checkCode$.pipe(
      map(code => code.trim()),
      filter(code => this.isCodeValid(code)),
      switchMap(code => concat(
        of({ checking: true }),
        CheckLocationCode(this.props.companyID, code).pipe(
          map(result => ({ checking: false, result: result })),
          catchError(() => of({ checking: false }))
        )
      ))
    ).subscribe(
      (o: { checking: boolean, result?: LocationExists }) => this.setState({
        codeChecking: o.checking,
        codeAvailable: (o.result?.LocationID || 0) === 0
      }),
      (error) => this.handleAPIError(error, { codeChecking: false, codeAvailable: undefined })
    )
  }

  loadLocations = () => {
    this.setState({ loading: true })
    this.searchLocationsSubscription = AutoFilterLocationsGetObservable(this.props.companyID, "", "", this.props.portalID).subscribe(
      ( result: AutoFilterLocation[]) => {
        if (this.props.locationID) {
          this.setState({ loading: false, locations: result.filter(loc => loc.AccountID === this.props.locationID).map(loc => loc.Name) || [] })
        } else {
          this.setState({ loading: false, locations: result.map(c => c.Name).filter((v, i, a) => a.indexOf(v) === i) || [] })
        }
      },
      (error) => this.handleAPIError(error, { loading: false, locations: [] })
    )
  }

  componentWillUnmount() {
    this.statesSubscription && this.statesSubscription.unsubscribe()
    this.timeZoneSubscription && this.timeZoneSubscription.unsubscribe()
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.searchLocationsSubscription && this.searchLocationsSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.nameExistsSubscription && this.nameExistsSubscription.unsubscribe()
    this.codeExistsSubscription && this.codeExistsSubscription.unsubscribe()
    this.inactivateSubscription && this.inactivateSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: LocationInformationFormProps, prevState: LocationInformationFormState) {
    if (this.props.locationID && this.state.locations !== prevState.locations) {
      this.handleLocationChange(this.state.locations)
    }
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }

  mapState = (info: LocationData, states: State[]): string => states.find(item => item.StateProvinceTerritoryID === info.StateProvinceTerritoryID)?.Abbreviation || info.State || ""

  mapLocationData = (data?: LocationData[]): LocationInformationData => {
    if (!data || data.length !== 1) {
      return { ...this.emptyData, name: this.state.name }
    }
    const info = data[0]
    return {
      name: info.Name || "",
      code: info.Code,
      streetAddress1: info.Address1 || "",
      streetAddress2: info.Address2 || "",
      city: info.City || "",
      state: this.mapState(info, this.state.states),
      zip: info.Zip || "",
      province: info.Province || "",
      country: info.Country || "",
      timeZoneID: info.TimeZoneID,
      webSiteURL: info.WebsiteURL || "",
      fax: info.Fax || "",
      officePhone: info.Phone || "",
      locationData: data[0],
      contactDerPhone: info.ContactDerPhone || "",
      contactDerFirstName: info.ContactDerFirstName || "",
      contactDerLastName: info.ContactDerLastName || "",
      contactDerEmail: info.ContactDerEmail || "",
    };
  }

  handleLocationChange = (items: string[]) => {
    const selectedLocation = items[0]
    if (selectedLocation) {
      this.setState({ selectedLocation: selectedLocation })
      this.locationName$.next(selectedLocation)
    } else {
      this.setState({ selectedLocation: undefined })
    }
  }
  handleLocationNameTyping = (location: string) => {
    this.setState({ name: location })
  }

  handleRetrievedCompanyAddressSelect = () => this.setState({
    retrievedLocations: undefined,
    ...this.mapLocationData(this.state.locationData ? [this.state.locationData] : undefined)
  })

  handleAddressSelect = (address: Address | string | undefined) => {
    if (address) {
      if (typeof address === 'string') {
        this.setState({ streetAddress1: address })
      } else if (address as Address) {
        this.setState({ streetAddress1: address.StreetLine || "", state: address.State || "", city: address.City || "" })
      }
    } else {
      this.setState({ streetAddress1: "" })
    }
  }

  performInactivate = () => {
    this.setState({ inactivating: true, showInactivateConfirmation: false })
    this.inactivateSubscription && this.inactivateSubscription.unsubscribe()
    this.inactivateSubscription = CompanyInactivate(this.state.locationData?.ImplementationID || 0, this.state.locationData?.AccountID || 0, true)
    .subscribe(
      data => {
        this.setState({ inactivating: false })
        toast.success(data)
      },
      error => this.handleAPIError(error, { inactivating: false })
    )
  }

  performActivate = () => {
    this.setState({ activating: true })
    this.inactivateSubscription && this.inactivateSubscription.unsubscribe()
    this.inactivateSubscription = CompanyActivationObservable(this.state.locationData?.ImplementationID || 0, this.state.locationData?.AccountID || 0, true)
    .subscribe(
      data => {
        this.setState({ activating: false })
        toast.success(data)
      },
      error => this.handleAPIError(error, { activating: false })
    )
  }

  handleCancel = () => this.props.onCancel()

  handleUpdate = () => {
    const { locationData, name, code, streetAddress1, streetAddress2, city, state, zip, officePhone, 
      province, country, timeZoneID, webSiteURL, fax,
      contactDerPhone, contactDerFirstName, contactDerLastName, contactDerEmail  } = this.state
    if (!this.props.add && !locationData) {
      return
    }

    const saveData: LocationData = {
      AccountID: this.props.add ? 0 : locationData?.AccountID || 0,
      ImplementationID: this.props.portalID,
      CompanyID: this.props.companyID,
      Name: name || locationData?.Name || "",
      Code: this.props.add ? code : locationData?.Code || "",
      Address1: streetAddress1,
      Address2: streetAddress2,
      City: city,
      State: state,
      Zip: zip,
      Phone: officePhone.replace(/-/g, ""),
      Province: province ? province : undefined,
      Country: country ? country : undefined,
      TimeZoneID: timeZoneID,
      WebsiteURL: webSiteURL ? webSiteURL : undefined,
      Fax: fax ? fax : undefined,
      StateProvinceTerritoryID: 0,
      Inactive: locationData?.Inactive || false,
      ContactDerFirstName: contactDerFirstName || "",
      ContactDerLastName: contactDerLastName || "",
      ContactDerPhone: (contactDerPhone || "").replace(/-/g, ""),
      ContactDerEmail : contactDerEmail || "",
    }

    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    const observable = this.props.add ? LocationCreate : LocationUpdate
    this.saveSubscription = observable(saveData).subscribe(
      (data) => {
        this.setState({ saving: false })
        toast.success(data)
        this.props.onSuccess && this.props.onSuccess()
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  handleHideAddLocation = () => {
    this.setState({ showAddLocation: false })
  }

  handleLocationAddSuccess = () => {
    this.handleHideAddLocation()
    this.loadLocations()
  }

  isNameTextValid = (value: string) => {
    const regexp = new RegExp(CompanyNameRegexp)
    return regexp.test(value)
  }
  isNameLengthValid = (value: string) => {
    const len = value.trim().length
    return len >= 3 && len <= 32
  }

  isNameValid = (value: string) => this.isNameLengthValid(value) && this.isNameTextValid(value)
  isCodeValid = (value: string) => value.length > 0
  isPhoneValid = (value: string) =>  (!!(value || "").length && ((value || "").replace(/\D/g, "").length === 10 || PhoneRegexp.test(value || "")))
  isEmailValid = (value: string) => {
    if(!value){
      return true
    }
    return EmailRegexp.test(value)
  }
  render() {
    const { name, code, city, state, streetAddress1, streetAddress2, states, statesLoading, zip, province, country, officePhone, fax, timeZoneID, webSiteURL,
      loading, saving, locationData, timeZonesLoading, timeZones, retrievedLocations, activating, inactivating,
      nameChecking, nameAvailable, codeChecking, codeAvailable, showLabAccountSelection, labAccountsLoading,
      contactDerPhone, contactDerFirstName, contactDerLastName, contactDerEmail } = this.state

    const { add, locationID } = this.props

    const disabled = !add && (!locationData || !!retrievedLocations)
    const validName = disabled || this.isNameValid(name)
    const validCode = disabled || !add || this.isCodeValid(code) && codeAvailable
    const validStreetAddress1 = disabled || !!streetAddress1.length
    const validCity = disabled || !!city.length
    const validState = disabled || !!state
    const validZip = disabled || (!!zip.length && ValidZipCode(zip))
    const validPhone = disabled || (!!officePhone.length && (officePhone.replace(/\D/g, "").length === 10 || PhoneRegexp.test(officePhone)))
    const validFax = disabled || fax.length === 0 || (fax.replace(/\D/g, "").length === 10 || PhoneRegexp.test(fax))

    const saveButtonName = add ? "Save" : "Update"
    const derPhone = (contactDerPhone || "").trim()
    const validContactDerFirstName = disabled || !!contactDerFirstName.length
    const validContactDerLastName = disabled || !!contactDerLastName.length
    const validContactDerPhone = disabled || (!!derPhone.length && this.isPhoneValid(derPhone))
    const validContactDerEmail = disabled || (!!contactDerEmail.length && this.isEmailValid(contactDerEmail))

    const formValid =
      validStreetAddress1 &&
      validCity &&
      validState &&
      validZip &&
      validPhone &&
      validFax &&
      validName &&
      validCode &&
      validContactDerFirstName &&
      validContactDerLastName &&
      validContactDerPhone &&
      validContactDerEmail

    return (<>
      <Modal.Body>
        <Form>
          <Form.Row className="form-group required">
            <Col lg={4} className="col-form-label">
              <Form.Label className="mb-0" htmlFor="locationName">Location</Form.Label> {code && !add ? `(${code})` : ""}
            </Col>
            <Col lg={8}>
              {add ? <>
                <Form.Control 
                  id="locationName"
                  type="text"
                  value={name}
                  isInvalid={!validName}
                  disabled={disabled}
                  autoComplete="new-password"
                  onChange={e => { this.setState({ name: e.target.value, nameAvailable: undefined }) }} 
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => { if (nameAvailable === undefined) this.checkName$.next(e.target.value)} }/>
                  {!nameChecking && nameAvailable && <Form.Control.Feedback type="valid" className="d-block">Name is available</Form.Control.Feedback>}
                  {!nameChecking && nameAvailable === false && <Form.Control.Feedback type="invalid" className="d-block">Name is already in use</Form.Control.Feedback>}
                  {!nameChecking && !validName && <Form.Control.Feedback type="invalid" className="d-block">Location Name must be of 3 to 32 characters and cannot contain any special characters except apostrophe, dash, comma or dot  [ ' or - or , or . ]</Form.Control.Feedback>}
                  </>
                : (locationID ? <Form.Control id="locationName" type="text" value={name} isInvalid={!validName} disabled={true} autoComplete="new-password" /> 
                : <InputGroup>
                  <Typeahead id="location-select"
                    ref={this.typeahead}
                    filterBy={(option, props) => props.selected.length > 0 || option.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    isLoading={this.state.loading}
                    onChange={this.handleLocationChange}
                    onInputChange={this.handleLocationNameTyping}
                    options={this.state.locations || []}
                    selected={this.state.selectedLocation ? [this.state.selectedLocation] : []}
                    minLength={0}
                    emptyLabel="No Locations Found"
                    placeholder=""
                    inputProps={{ autoComplete: "chrome-off" }}>
                    {!loading &&
                      <Button variant="toggle" onClick={() => { this.typeahead && this.typeahead.current && (this.typeahead.current as any).toggleMenu() }}>
                        <FaAngleDown />
                      </Button>}
                  </Typeahead>
                </InputGroup>)}
            </Col>
          </Form.Row>
          {add && <Form.Row className="form-group required">
            <Form.Label column lg={4} htmlFor="locationCode">Code</Form.Label>
            <Col lg={8}>
              <Form.Control
                id="locationCode" type="text"
                value={code}
                onChange={e => { this.setState({ code: e.target.value, codeAvailable: undefined }) }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => { if (codeAvailable === undefined) this.checkCode$.next(e.target.value) }}
                autoComplete="new-password"
                isInvalid={!validCode}
                disabled={disabled} />
              {!codeChecking && codeAvailable && <Form.Control.Feedback type="valid" className="d-block">Code is available</Form.Control.Feedback>}
              {!codeChecking && codeAvailable === false && <Form.Control.Feedback type="invalid" className="d-block">Code is already in use</Form.Control.Feedback>}
            </Col>
          </Form.Row>}
          <Form.Row className="form-group required">
            <Form.Label column lg={4}>Street Address 1</Form.Label>
            <Col lg={8}>
              <AddressInput onAddressChange={this.handleAddressSelect}
                isInvalid={!validStreetAddress1}
                disabled={disabled}
                selected={{ City: city, State: state, StreetLine: streetAddress1, Text: streetAddress1 }} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column lg={4} htmlFor="streetAddress2">Street Address 2</Form.Label>
            <Col lg={8}>
              <Form.Control id="streetAddress2" type="text"
                autoComplete="new-password"
                value={streetAddress2}
                disabled={disabled}
                onChange={e => this.setState({ streetAddress2: e.target.value })} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column lg={4} htmlFor="city">City</Form.Label>
            <Col lg={8}>
              <Form.Control id="city" type="text"
                autoComplete="new-password"
                value={city}
                onChange={e => this.setState({ city: e.target.value })}
                disabled={disabled}
                isInvalid={!validCity} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column lg={4} htmlFor="state">State</Form.Label>
            <Col sm={5}>
              <StateSelect selectedCode={state}
                states={states}
                onChange={selected => this.setState({ state: selected?.Abbreviation || "" })}
                isDisabled={disabled || statesLoading}
                isInvalid={!validState} />
            </Col>
            <Form.Label column sm={1} htmlFor="zip">ZIP</Form.Label>
            <Col sm={2}>
              <Form.Control id="zip" type="text"
                autoComplete="new-password"
                onChange={e => this.setState({ zip: e.target.value })}
                value={zip}
                disabled={disabled}
                isInvalid={!validZip} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column lg={4} htmlFor="province">Province</Form.Label>
            <Col lg={8}>
              <Form.Control id="province" type="text"
                autoComplete="new-password"
                value={province || ""}
                onChange={e => this.setState({ province: e.target.value })}
                disabled={disabled} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column lg={4} htmlFor="country">Country</Form.Label>
            <Col lg={8}>
              <Form.Control id="province" type="text"
                autoComplete="new-password"
                value={country || ""}
                onChange={e => this.setState({ country: e.target.value })}
                disabled={disabled} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column lg={4} htmlFor="officePhone">Office Phone #</Form.Label>
            <Col lg={8}>
              <PhoneNumberMaskedInput
                autoComplete="new-password"
                value={officePhone}
                onChange={(value: string) => this.setState({ officePhone: value })}
                disabled={disabled}
                isInvalid={!validPhone} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column lg={4} htmlFor="faxPhone">Fax Phone #</Form.Label>
            <Col lg={8}>
              <PhoneNumberMaskedInput
                autoComplete="new-password"
                value={fax}
                onChange={(value: string) => this.setState({ fax: value })}
                disabled={disabled}
                isInvalid={!validFax} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column lg={4}>Time Zone</Form.Label>
            <Col lg={8}>
              <Form.Control id="timeZones" as="select"
                disabled={timeZonesLoading || disabled}
                value={timeZoneID}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { this.setState({ timeZoneID: Number(e.target.value) }) }}>
                <option value={0} key={0}></option>
                {timeZones?.map(tz => <option value={tz.TimeZoneID} key={tz.TimeZoneID}>{tz.Abbreviation}</option>)}
              </Form.Control>
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column lg={4} htmlFor="website">Website</Form.Label>
            <Col lg={8}>
              <Form.Control id="website" type="text"
                autoComplete="new-password"
                value={webSiteURL || ""}
                onChange={e => this.setState({ webSiteURL: e.target.value })}
                disabled={disabled} />
            </Col>
          </Form.Row>
          {!add &&
            <Form.Row className="form-group">
              <Form.Label column lg={4} >Lab Accounts</Form.Label>
              <Col lg={4}>
                <ActionButton variant="secondary" onClick={() => this.setState({ showLabAccountSelection: true })} disabled={disabled} spin={labAccountsLoading} disableOnSpin={false}>Lab Account Details</ActionButton>
              </Col>
            </Form.Row>}
            {<>
              <Form.Row className="form-group required">
                <Form.Label column lg={4} htmlFor="contactDerFirstName">
                  DER Contact First\Last Names
                </Form.Label>
                <Col lg={4}>
                  <Form.Control id="contactDerFirstName" type="text"
                    autoComplete="new-password"
                    value={contactDerFirstName}
                    onChange={e => this.setState({ contactDerFirstName: e.target.value })}
                    disabled={disabled}
                    isInvalid={!validContactDerFirstName} />
                </Col>
                <Col lg={4}>
                  <Form.Control id="contactDerLastName" type="text"
                    autoComplete="new-password"
                    value={contactDerLastName}
                    onChange={e => this.setState({ contactDerLastName: e.target.value })}
                    disabled={disabled}
                    isInvalid={!validContactDerLastName} />
                </Col>
              </Form.Row>
              <Form.Row className="form-group required">
                <Form.Label column lg={4} htmlFor="contactDerPhone">
                  DER Contact Phone #
                </Form.Label>
                <Col lg={8}>
                  <PhoneNumberMaskedInput
                    autoComplete="new-password"
                    value={contactDerPhone}
                    onChange={(value: string) => this.setState({ contactDerPhone: value })}
                    disabled={disabled}
                    isInvalid={!validContactDerPhone} />
                </Col>
              </Form.Row>
              <Form.Row className="form-group required">
                <Form.Label column lg={4} htmlFor="contactDerEmail">
                  DER Contact Email #
                </Form.Label>
                <Col lg={8}>
                <Form.Control id="contactDerEmail" type="text"
                    autoComplete="new-password"
                    value={contactDerEmail}
                    onChange={e => this.setState({ contactDerEmail: e.target.value })}
                    disabled={disabled}
                    isInvalid={!validContactDerEmail} />
                </Col>
              </Form.Row>
            </>}
        </Form>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div>
          {!add && !locationID && <Button variant="primary" onClick={() => this.setState({ showAddLocation: true })}>Add New Location</Button>}
        </div>
        <div>
          {!!this.props.adminMode && <>
            {!locationData?.Inactive && <ActionButton variant="secondary" disabled={disabled} onClick={() => this.setState({ showInactivateConfirmation: true })} spin={inactivating} disableOnSpin className="mr-2">Inactivate</ActionButton>}
            {locationData?.Inactive && <ActionButton variant="secondary" onClick={() => this.performActivate()} spin={activating} disableOnSpin className="mr-2">Activate</ActionButton>}
          </>}
          <Button variant="secondary" onClick={() => this.handleCancel()} className="mr-2">Cancel</Button>
          <ActionButton variant="primary" onClick={this.handleUpdate} disabled={!formValid || disabled} spin={saving} disableOnSpin>{saveButtonName}</ActionButton>
        </div>
      </Modal.Footer>
      {!add &&
        <LabAccountSelection
          locationMode
          name={locationData?.Name || ""}
          implementationID={locationData?.ImplementationID || 0}
          accountID={locationData?.AccountID || 0}
          show={showLabAccountSelection}
          onClose={() => this.setState({ showLabAccountSelection: false })} />}
      <ConfirmationModal show={this.state.showInactivateConfirmation}
        title="CONFIRM INACTIVATE"
        textTitle="Are you sure you want to inactivate?"
        text=""
        onConfirm={this.performInactivate}
        onHide={() => this.setState({ showInactivateConfirmation: false })} />
      <AddressMatchModal
        show={!!retrievedLocations && retrievedLocations.length > 1}
        addresses={retrievedLocations?.map(item => ({ ID: item.AccountID.toString(), Address1: item.Address1, Zip: item.Zip } as AddressData)) || []}
        selectedID={locationData?.AccountID.toString()}
        title="EXISTING LOCATION ADDRESS"
        subTitle="The location name you have entered already exists as the following addresses:"
        message="Please select the address you want to activate"
        onAddressSelect={(selected: AddressData) => this.setState({ locationData: retrievedLocations?.find(item => item.AccountID.toString() === selected.ID) })}
        onClose={this.handleRetrievedCompanyAddressSelect} />
      {!this.props.add && <AddLocationModal portalID={this.props.portalID} companyID={this.props.companyID} show={this.state.showAddLocation}
        onHide={this.handleHideAddLocation} onSuccess={this.handleLocationAddSuccess} />}
    </>
    )
  }
}
